<template>
  <DraggableWindow
    v-show="show"
    :title="windowTitle"
    :initial-x="initialPos.x"
    :initial-y="initialPos.y"
    :initial-width="initialWidth"
    :initial-height="initialHeight"
    :z-index="zIndex"
    :hide-body="hideBody"
    @focus="$emit('focus')"
    class="pointer-events-auto"
    :class="['participant-window', isLocal ? 'local-window' : 'remote-window']"
  >
    <template #header-left>
      <slot name="header-left">
        <div class="header-left">
          <UserAvatarBadge :avatar="participant.avatar || ''" size="sm" />
          <div class="header-name">{{ displayName }}</div>
        </div>
      </slot>
    </template>

    <template #body>
      <div class="media-vessel">
        <video
          ref="videoEl"
          autoplay
          muted
          playsinline
          class="video-el"
          @loadedmetadata="onVideoEvent"
          @loadeddata="onVideoEvent"
          @playing="onVideoEvent"
          @timeupdate="onVideoEvent"
          @resize="onVideoEvent"
        />
        <!-- Remote audio playback: keep video muted for echo safety, play audio via hidden element -->
        <audio v-if="!isLocal" ref="audioEl" autoplay style="display: none" />
        <div v-show="showNoVideoOverlay" class="no-video">
          <template v-if="isLocal">
            <span v-if="localOverlayText">{{ localOverlayText }}</span>
          </template>
          <template v-else>
            <div class="call-status row">
              <UserAvatarBadge :avatar="participant.avatar" size="md" />
              <div class="call-status__text">{{ remoteOverlayText }}</div>
            </div>
          </template>
        </div>
      </div>
    </template>

    <template #controls>
      <q-btn
        dense
        flat
        round
        size="xs"
        class="no-drag pw-control-btn"
        color="white"
        :icon="collapsed ? 'unfold_more' : 'unfold_less'"
        :aria-label="collapsed ? 'Show camera view' : 'Hide camera view'"
        @click.stop="toggleCollapsed"
      />
      <slot name="controls">
        <q-btn
          v-if="!isLocal"
          dense
          flat
          round
          size="xs"
          class="pw-control-btn"
          icon="call_end"
          color="negative"
          @click="$emit('hangup')"
        />
      </slot>
    </template>

    <template #footer v-if="isLocal">
      <slot name="footer" />
    </template>
  </DraggableWindow>
</template>

<script setup>
  import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
  import DraggableWindow from './DraggableWindow.vue'
  import UserAvatarBadge from '@/components/ui/badges/UserAvatarBadge.vue'
  import { createDebugLogger } from '../config/debugLog'

  const sfuDebug = createDebugLogger('sfu')

  const props = defineProps({
    participant: { type: Object, required: true },
    isLocal: { type: Boolean, default: false },
    initialPos: { type: Object, default: () => ({ x: 20, y: 20 }) },
    initialWidth: { type: Number, default: 420 },
    initialHeight: { type: Number, default: 200 },
    zIndex: { type: Number, default: 10 },
    show: { type: Boolean, default: true },
    isCallerPreAnswer: { type: Boolean, default: false },
    callStatus: { type: String, default: '' },
    videoOn: { type: Boolean, default: true },
    localReady: { type: Boolean, default: true },
    remoteTracksChangedAt: { type: [Object, Number], default: 0 }
  })

  const emit = defineEmits(['focus', 'hangup', 'video-ready'])
  const displayName = computed(() => {
    const n = String(props.participant?.name || '').trim()
    if (n) return n
    const uid = String(props.participant?.uid || '').trim()
    if (uid) return uid
    return 'User'
  })

  const videoEl = ref(null)
  const audioEl = ref(null)
  const collapsed = ref(false)
  const firstFrameShown = ref(false)
  const hasFrames = ref(false)
  const streamTrackTick = ref(0)
  const monitorTick = ref(0)
  const lastRenderableAt = ref(0)
  let lastFrames = 0
  let lastChangeAt = 0
  let monitorTimer = null
  let remoteReattachTimer = null
  let localReattachTimer = null
  const STALL_MS = 2000
  const MONITOR_MS = 100
  const RENDER_GRACE_MS = 1500
  const REMOTE_REATTACH_MS = 400
  const HAVE_CURRENT_DATA =
    typeof HTMLMediaElement === 'undefined' ? 2 : HTMLMediaElement.HAVE_CURRENT_DATA

  const isParticipantCalling = computed(() => {
    if (props.isLocal) return props.isCallerPreAnswer
    const status = String(props.callStatus || '').toLowerCase()
    const preAnswer = status === 'ringing' || status === 'connecting'
    if (!preAnswer) return false
    return !props.participant.stream
  })

  // Track presence of any candidate video tracks (even if muted) so remote
  // windows can render immediately when camera toggles on.
  const hasVideoTrack = computed(() => {
    void streamTrackTick.value
    void props.remoteTracksChangedAt
    const stream = props.participant.stream
    if (!stream || typeof stream.getVideoTracks !== 'function') return false
    const tracks = stream.getVideoTracks() || []
    return tracks.some((t) => !!t && t.readyState !== 'ended')
  })

  const windowTitle = computed(() => {
    if (props.isLocal) return props.participant.name
    if (isParticipantCalling.value) return `Calling ${props.participant.name || '...'}`
    return props.participant.name
  })

  const hasRecentRenderable = computed(() => {
    void monitorTick.value
    const ts = Number(lastRenderableAt.value || 0)
    if (!ts) return false
    return Date.now() - ts < RENDER_GRACE_MS
  })

  // Stream is considered live when a video track is enabled and not ended.
  // Place this before cameraState to avoid TDZ when cameraState references it.
  const isStreamLive = computed(() => {
    void streamTrackTick.value
    void props.remoteTracksChangedAt
    const stream = props.participant.stream
    if (!stream || typeof stream.getVideoTracks !== 'function') return false
    const tracks = stream.getVideoTracks() || []
    return tracks.some((t) => !!t && t.readyState !== 'ended' && t.enabled !== false)
  })

  const cameraState = computed(() => {
    const hasStream = !!props.participant.stream
    if (props.isLocal) {
      if (!hasStream || !props.videoOn) return 'off'
      if (!hasVideoTrack.value) return 'warming'
      if (!firstFrameShown.value) return 'warming'
      if (!props.localReady) return 'warming'
      if (isStreamLive.value || hasRecentRenderable.value) return 'live'
      return 'warming'
    }

    if (isParticipantCalling.value) return 'calling'
    if (!isStreamLive.value && !hasRecentRenderable.value) {
      if (!hasVideoTrack.value) return 'off'
      if (firstFrameShown.value) return 'off'
    }
    if (!firstFrameShown.value) return 'warming'
    if (isStreamLive.value || hasRecentRenderable.value) return 'live'
    return 'warming'
  })

  // Debug camera state transitions to understand overlay behavior
  watch(
    () => cameraState.value,
    (st) => {
      try {
        sfuDebug('[ParticipantWindow] cameraState', {
          isLocal: props.isLocal,
          state: st,
          localReady: props.localReady,
          firstFrameShown: firstFrameShown.value
        })
      } catch {}
    },
    { immediate: true }
  )

  // Keep the body mounted for both local and remote participants so the <video>
  // element is never unmounted during camera toggles.
  const hideBody = computed(() => {
    return collapsed.value
  })

  function toggleCollapsed() {
    collapsed.value = !collapsed.value
    if (!collapsed.value) {
      const s = props.participant?.stream || null
      attachStreamToVideo(s).catch(() => {})
    }
  }

  const showRemoteOverlay = computed(() => {
    if (props.isLocal) return false
    return cameraState.value !== 'live'
  })

  const showLocalOverlay = computed(() => {
    if (!props.isLocal) return false
    return cameraState.value !== 'live'
  })

  const showNoVideoOverlay = computed(() => {
    return props.isLocal ? showLocalOverlay.value : showRemoteOverlay.value
  })

  // Do not hide the <video> element for decode; keep it visible and
  // overlay state on top. This avoids layout jumps and keeps Quasar
  // window rendering straightforward.

  const localOverlayText = computed(() => {
    if (!props.isLocal) return ''
    if (!props.localReady) return 'Preparing video…'
    // When localReady is true but camera isn't live yet, indicate warm-up
    if (cameraState.value === 'off') return 'Camera off'
    if (cameraState.value !== 'live') return 'Starting video…'
    return ''
  })

  const remoteOverlayText = computed(() => {
    if (props.isLocal) return ''
    const name = displayName.value
    const waitingForFirstVideo = !hasVideoTrack.value && !firstFrameShown.value
    if (isParticipantCalling.value || waitingForFirstVideo || cameraState.value === 'warming') {
      return `Calling ${name}`
    }
    if (cameraState.value === 'off') return 'Camera off'
    return name
  })

  // Visual activity detection

  watch(isStreamLive, (live) => {
    if (!live) return
    if (videoEl.value) {
      try {
        videoEl.value.play().catch(() => {})
      } catch {}
    }
    onVideoEvent()
  })

  function getDecodedFrames(el) {
    try {
      if (!el) return 0
      if (typeof el.getVideoPlaybackQuality === 'function') {
        const q = el.getVideoPlaybackQuality()
        if (q && typeof q.totalVideoFrames === 'number') return q.totalVideoFrames
      }
      return el.webkitDecodedFrameCount || 0
    } catch {
      return 0
    }
  }

  function onVideoEvent(e) {
    const el = videoEl.value
    if (!el) return
    void e
    // If the browser reports width/height and we aren't paused, it's likely showing something.
    // Also check if we have actually decoded some frames if available.
    if ((el.videoWidth > 0 && el.videoHeight > 0) || el.readyState >= HAVE_CURRENT_DATA) {
      if (el.paused) {
        try {
          el.play().catch(() => {})
        } catch {}
      }
      lastRenderableAt.value = Date.now()
      if (!firstFrameShown.value) {
        firstFrameShown.value = true
        try {
          sfuDebug('[ParticipantWindow] first frame flagged via event', {
            isLocal: props.isLocal
          })
        } catch {}
        emit('video-ready')
      }
    }
  }

  function resetFrameDetection() {
    firstFrameShown.value = false
    hasFrames.value = false
    lastRenderableAt.value = 0
    lastFrames = 0
    lastChangeAt = 0
  }

  async function attachStreamToVideo(stream, { forceReattach = false } = {}) {
    await nextTick()
    const el = videoEl.value
    if (!el) return
    try {
      if (forceReattach && stream && el.srcObject === stream) {
        el.srcObject = null
      }
    } catch {}
    if (el.srcObject !== (stream || null)) {
      el.srcObject = stream || null
    }
    el.autoplay = true
    el.playsInline = true
    el.muted = true
    try {
      await el.play().catch(() => {})
    } catch {}
    // Remote audio: attach to hidden audio element so sound plays while video stays muted
    if (!props.isLocal) {
      const au = audioEl.value
      if (au) {
        try {
          if (forceReattach && stream && au.srcObject === stream) {
            au.srcObject = null
          }
        } catch {}
        if (au.srcObject !== (stream || null)) {
          au.srcObject = stream || null
        }
        try {
          au.autoplay = true
          au.muted = false
          au.volume = 1.0
          await au.play().catch(() => {})
        } catch {}
      }
    }
    try {
      sfuDebug('[ParticipantWindow] attach stream', {
        isLocal: props.isLocal,
        hasStream: !!stream,
        readyState: el.readyState,
        width: el.videoWidth,
        height: el.videoHeight
      })
    } catch {}
  }

  function scheduleRemoteReattach(stream) {
    if (remoteReattachTimer) {
      clearTimeout(remoteReattachTimer)
      remoteReattachTimer = null
    }
    if (props.isLocal || !stream) return
    remoteReattachTimer = setTimeout(() => {
      const el = videoEl.value
      if (!el) return
      const hasRenderable =
        (el.videoWidth > 0 && el.videoHeight > 0) || el.readyState >= HAVE_CURRENT_DATA
      if (hasRenderable) return
      attachStreamToVideo(stream, { forceReattach: true }).catch(() => {})
    }, REMOTE_REATTACH_MS)
  }

  onMounted(() => {
    const s = props.participant.stream
    if (s) {
      attachStreamToVideo(s).catch(() => {})
      scheduleRemoteReattach(s)
    }

    monitorTimer = setInterval(() => {
      const el = videoEl.value
      if (!el) return
      const now = Date.now()
      monitorTick.value = now

      const count = getDecodedFrames(el)
      if (count > lastFrames) {
        lastFrames = count
        lastChangeAt = now
        hasFrames.value = true
        lastRenderableAt.value = now
        if (!firstFrameShown.value) {
          firstFrameShown.value = true
          try {
            sfuDebug('[ParticipantWindow] first frame flagged via monitor', {
              isLocal: props.isLocal,
              frames: count
            })
          } catch {}
          emit('video-ready')
        }
      } else if (now - lastChangeAt > STALL_MS) {
        hasFrames.value = false
      }
    }, MONITOR_MS)
  })

  onUnmounted(() => {
    if (monitorTimer) clearInterval(monitorTimer)
    if (remoteReattachTimer) clearTimeout(remoteReattachTimer)
    if (localReattachTimer) clearTimeout(localReattachTimer)
    try {
      const au = audioEl.value
      if (au) {
        au.pause?.()
        au.srcObject = null
      }
    } catch {}
  })

  // Ensure srcObject is set
  // When remoteTracksChangedAt updates, bump the track tick to trigger hasVideoTrack recompute
  // CRITICAL: Also reset frame detection for remote participants when tracks change.
  // This handles the case where video is added to existing audio stream (same stream ref).
  watch(
    () => props.remoteTracksChangedAt,
    () => {
      streamTrackTick.value = Date.now()
      resetFrameDetection()
      const s = props.participant?.stream || null
      if (s) {
        attachStreamToVideo(s, { forceReattach: true }).catch(() => {})
        scheduleRemoteReattach(s)
      }
    }
  )

  watch(
    () => props.participant.stream,
    (s, oldValue, onCleanup) => {
      try {
        sfuDebug('[ParticipantWindow] attach stream', {
          isLocal: props.isLocal,
          hasStream: !!s
        })
      } catch {}
      if (s !== oldValue) {
        // Any stream swap must restart first-frame detection.
        resetFrameDetection()
      }
      streamTrackTick.value = Date.now()
      attachStreamToVideo(s).catch(() => {})
      scheduleRemoteReattach(s)
      try {
        const tr = s?.getVideoTracks?.()[0]
        if (tr)
          sfuDebug('[ParticipantWindow] track state', {
            enabled: tr.enabled,
            readyState: tr.readyState
          })
      } catch {}

      // Standard Vue cleanup for event listeners
      const checkActive = () => {
        try {
          if (!s.active) {
            resetFrameDetection()
          }
        } catch {}
      }

      if (s && typeof s.addEventListener === 'function') {
        try {
          s.addEventListener('inactive', checkActive)
        } catch {}
      }

      onCleanup(() => {
        if (s && typeof s.removeEventListener === 'function') {
          try {
            s.removeEventListener('inactive', checkActive)
          } catch {}
        }
      })

      const bumpTrackTick = () => {
        streamTrackTick.value = Date.now()
        // Vue-safe play attempt
        if (videoEl.value) {
          videoEl.value.play().catch(() => {})
        }
      }

      // Track listeners management
      const cleanupTrackListeners = []

      const addTrackListeners = (track) => {
        if (!track || typeof track.addEventListener !== 'function') return

        const onTrackState = () => bumpTrackTick()

        try {
          track.addEventListener('ended', onTrackState)
          track.addEventListener('mute', onTrackState)
          track.addEventListener('unmute', onTrackState)
        } catch {}

        cleanupTrackListeners.push(() => {
          try {
            track.removeEventListener('ended', onTrackState)
            track.removeEventListener('mute', onTrackState)
            track.removeEventListener('unmute', onTrackState)
          } catch {}
        })
      }
      const onAddTrack = (e) => {
        addTrackListeners(e?.track || null)
        bumpTrackTick()
      }

      const onRemoveTrack = () => bumpTrackTick()

      if (s && typeof s.addEventListener === 'function') {
        s.addEventListener('addtrack', onAddTrack)
        s.addEventListener('removetrack', onRemoveTrack)

        try {
          const tracks = s.getVideoTracks?.() || []
          tracks.forEach((t) => addTrackListeners(t))
        } catch {}
      }

      onCleanup(() => {
        if (s && typeof s.removeEventListener === 'function') {
          s.removeEventListener('addtrack', onAddTrack)
          s.removeEventListener('removetrack', onRemoveTrack)
        }

        // Execute all collected track cleanup functions
        cleanupTrackListeners.forEach((cleanup) => {
          try {
            cleanup()
          } catch {}
        })
      })
      // No signal usage; use onCleanup if needed
    },
    { immediate: true }
  )

  // Local camera toggles can keep the same stream reference; force a reattach
  // when turning camera back on so the element resumes decoding reliably.
  watch(
    () => props.videoOn,
    (isOn) => {
      if (!props.isLocal) return
      const s = props.participant?.stream || null
      if (!s) return
      if (localReattachTimer) {
        clearTimeout(localReattachTimer)
        localReattachTimer = null
      }
      if (!isOn) {
        resetFrameDetection()
        return
      }
      resetFrameDetection()
      attachStreamToVideo(s, { forceReattach: true }).catch(() => {})
      localReattachTimer = setTimeout(() => {
        attachStreamToVideo(s, { forceReattach: true }).catch(() => {})
        localReattachTimer = null
      }, 250)
    }
  )
</script>

<style scoped>
  .participant-window {
    pointer-events: auto; /* Ensure the window itself captures events */
  }
  .media-vessel {
    width: 100%;
    height: 100%;
    position: relative;
    background: #000;
  }
  .video-el {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: #000;
    position: relative;
    z-index: 0;
  }
  .video-el.hidden-decode {
    position: absolute;
    top: 0;
    left: 0;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }
  .no-video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.8);
    color: rgba(255, 255, 255, 0.9);
    font-size: 12px;
    z-index: 1;
  }
  .header-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .header-name {
    font-weight: 600;
  }
  .call-status {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #fff;
    font-size: 14px;
  }
  .call-status__text {
    font-weight: 600;
  }
  .pw-control-btn {
    min-width: 22px;
    min-height: 22px;
    width: 22px;
    height: 22px;
  }
</style>
